import React, { useContext, useMemo, useState } from 'react'
import { SaveChartProps } from '../../../system-data-handler/types'
import { ChartView, getChartsEditingSettings, RechartData, useLegendState } from 'src/Libs/chart-kit'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import SystemChart from '../../shared/components/SystemChart'
import { useUserSettings } from 'src/Hooks'
import { getInitialHouseConsumptionPieChart } from '../utils/initial-chart-config'
import useChartLayout from 'src/Libs/chart-kit/shared/hooks/use-chart-layout'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { DASHBOARD_HOUSE_CONSUMPTION_PIE_CHART_ID, DASHBOARD_POC_HOUSE_CONSUMPTION_PIE_CHART_ID } from 'src/Libs/qa/ids'
import { Box } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { IconButton } from '@e3dc-react/shell/Elements/Buttons/Buttons'
import { J1TransferarrowB } from '@e3dc-react/icons'
import useWeightedPrices from '../hooks/use-weighted-prices'

interface Props {
  height: number
  data: RechartData
  view: ChartView
  resolution?: ChartView
  handleSaveChart: (props: SaveChartProps) => void
  from: Moment
  to: Moment
  showForecast: boolean
  legendHeight: number
  setBiggestLegendHeight: (height: number) => void
  isAiActive?: boolean
}

/**
 * Pie Chart for house consumption data
 */
const AutarkyPieChart: React.FC<Props> = ({
  height,
  data,
  view,
  resolution,
  handleSaveChart,
  from,
  to,
  showForecast,
  legendHeight,
  setBiggestLegendHeight,
  isAiActive = false,
}) => {
  const { t } = useCustomTranslation()
  const { userSettings, updateUserSettings } = useUserSettings()
  const chartSettingsList = useMemo(() => getChartsEditingSettings(userSettings), [userSettings])
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const initialChartLayout = useMemo(
    () => getInitialHouseConsumptionPieChart(height, isDynamicPowerPriceEnabled),
    [height, isDynamicPowerPriceEnabled]
  )
  const { chartLayout } = useChartLayout(chartSettingsList, initialChartLayout)
  const chartLayoutId = chartLayout.id ?? ''
  const { saveLegendState, hideLegend } = useLegendState(`${chartLayoutId}`)
  /** Only enable 'isWeightedAveragePriceOn', when the dpp is enabled aswell */
  const [isWeightedAveragePriceOn, setIsWeightedAveragePriceOn] = useState<boolean>(
    (userSettings.isAutarkyChartWeightedAveragePriceOn ?? false) && isDynamicPowerPriceEnabled
  )
  const { weightedPrices, isLoading: isPieChartWeightedPricesLoading } = useWeightedPrices({
    from,
    to,
    view: resolution ?? view,
    isEnabled: isWeightedAveragePriceOn,
  })
  const infoText =
    isDynamicPowerPriceEnabled && isAiActive
      ? t('system-charts.autarkie-pie-chart.tooltip-ai-active')
      : isDynamicPowerPriceEnabled && !isAiActive
      ? t('system-charts.autarkie-pie-chart.tooltip-ai-inactive')
      : undefined

  const dataQa = data.isPocChart ? DASHBOARD_POC_HOUSE_CONSUMPTION_PIE_CHART_ID : DASHBOARD_HOUSE_CONSUMPTION_PIE_CHART_ID

  const toggleWeightedAveragePrice = (): void => {
    const newValue = !isWeightedAveragePriceOn
    setIsWeightedAveragePriceOn(newValue)
    updateUserSettings({ isAutarkyChartWeightedAveragePriceOn: newValue })
  }

  const getLabelAction = (): React.ReactElement | undefined => {
    if (!isDynamicPowerPriceEnabled) return
    return <IconButton icon={<J1TransferarrowB color={isWeightedAveragePriceOn ? 'primary' : 'inherit'} />} onClick={toggleWeightedAveragePrice} />
  }

  return (
    <Box>
      <SystemChart
        view={view}
        resolution={resolution}
        to={to}
        from={from}
        showForecast={showForecast}
        rechartData={data}
        customLayout={chartLayout}
        loadingState={data.loadingState}
        handleSaveChart={props => handleSaveChart({ customLayout: chartLayout, chartSettings: props })}
        isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
        maxHeight={chartLayout.maxHeight}
        hideLegend={hideLegend}
        toggleHideLegend={() => saveLegendState()}
        dataQa={dataQa}
        setBiggestLegendHeight={setBiggestLegendHeight}
        fixedLegendHeight={legendHeight}
        pieChartTooltipText={infoText}
        pieChartLabelAction={getLabelAction()}
        isWeightedAveragePriceOn={isWeightedAveragePriceOn}
        weightedPrice={isPieChartWeightedPricesLoading ? 0 : weightedPrices?.weightedAveragePrice || 0}
        isAutarkyPieChart
      />
    </Box>
  )
}

export default AutarkyPieChart
